// slices/exams/examsSlice.ts
import {
  createSlice,
  PayloadAction,
  createEntityAdapter,
  createSelector,
} from '@reduxjs/toolkit';
import { UserExam } from '../../types/User';
import { logout } from '../auth/authSlice';
import {
  isStepExam,
  isShelfExam,
  isVerifiedSchoolExam,
  isCustomSchoolExam,
  isRCTExam,
  getExamAccessLevel,
} from '../../utils/examUtils';
import { SchoolState } from '../school/schoolSlice';
import { SubscriptionAccess } from '../../types/Subscription';

type ExamsStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

// Use createEntityAdapter for normalization boilerplate
const examsAdapter = createEntityAdapter<UserExam>();

// Define the shape of the normalized state
interface NormalizedExamsData {
  ids: string[];
  entities: Record<string, UserExam>;
  primaryExamId: string | null;
}

// Define the combined root state type for selectors that need access to multiple slices
interface RootState {
  exams: ExamsState;
  school: SchoolState;
}

interface ExamsState extends ReturnType<typeof examsAdapter.getInitialState> {
  primaryExamId: string | null;
  status: ExamsStatus;
  error: string | null;
}

const initialState: ExamsState = examsAdapter.getInitialState({
  primaryExamId: null,
  status: 'idle',
  error: null,
});

// Helper function to normalize API response (if needed outside slice)
// Can also be done directly within the reducer
export const normalizeExams = (exams: UserExam[]): NormalizedExamsData => {
  const normalized = examsAdapter.setAll(examsAdapter.getInitialState(), exams);
  const primaryExam = exams.find((exam) => exam.primary);
  return {
    ids: normalized.ids as string[],
    entities: normalized.entities as Record<string, UserExam>,
    primaryExamId: primaryExam ? primaryExam.id : null,
  };
};

export const examsSlice = createSlice({
  name: 'exams',
  initialState,
  reducers: {
    examsLoading: (state) => {
      state.status = 'loading';
      state.error = null;
    },
    // Action to set all exams (expects normalized data or raw array)
    examsReceived: (state, action: PayloadAction<UserExam[]>) => {
      const exams = action.payload;
      const primaryExam = exams.find((exam) => exam.primary);
      examsAdapter.setAll(state, exams); // Use adapter to set entities and ids
      state.primaryExamId = primaryExam ? primaryExam.id : null;
      state.status = 'succeeded';
      state.error = null;
    },
    examCreated: (state, action: PayloadAction<UserExam>) => {
      const newExam = action.payload;
      // If the new exam is primary, ensure all others are not
      if (newExam.primary) {
        Object.values(state.entities).forEach((exam) => {
          if (exam) {
            exam.primary = false;
          }
        });
        state.primaryExamId = newExam.id;
      }
      // Add the new exam
      examsAdapter.addOne(state, newExam);
    },
    examUpdated: (
      state,
      action: PayloadAction<Partial<UserExam> & { id: string }>
    ) => {
      // Use adapter to update a single exam
      examsAdapter.updateOne(state, {
        id: action.payload.id,
        changes: action.payload,
      });

      // If the 'primary' status was explicitly set to true in the update
      if (action.payload.primary === true) {
        // Ensure this exam is the only primary one
        Object.values(state.entities).forEach((exam) => {
          if (exam && exam.id !== action.payload.id) {
            exam.primary = false;
          }
        });
        // Update the primaryExamId
        state.primaryExamId = action.payload.id;
      }
      // If the 'primary' status was explicitly set to false and this was the primary exam
      else if (
        action.payload.primary === false &&
        state.primaryExamId === action.payload.id
      ) {
        // Remove the primaryExamId as this exam is no longer primary
        state.primaryExamId = null;
        // Note: We are not automatically assigning a new primary here.
        // If needed, additional logic could find another exam to set as primary.
      }
      // Ensure the updated entity reflects the changes (adapter might not update the primary flag correctly if not part of changes)
      // This is redundant if changes.primary is handled, but good for safety
      if (
        state.entities[action.payload.id] &&
        action.payload.primary !== undefined
      ) {
        state.entities[action.payload.id]!.primary = action.payload.primary;
      }

      // No need to update status here unless it's a specific requirement
      // state.status = 'succeeded';
    },
    examDeleted: (state, action: PayloadAction<string>) => {
      examsAdapter.removeOne(state, action.payload);
    },
    examsFailed: (state, action: PayloadAction<string | null>) => {
      state.status = 'failed';
      state.error = action.payload ?? 'Failed to load exams';
    },
    clearExams: (state) => {
      examsAdapter.removeAll(state); // Use adapter to clear
      state.primaryExamId = null;
      state.status = 'idle';
      state.error = null;
    },
    setPrimaryExam: (state, action: PayloadAction<string>) => {
      const newPrimaryId = action.payload;
      const oldPrimaryId = state.primaryExamId;

      // Ensure the new primary ID exists before proceeding
      if (state.entities[newPrimaryId]) {
        // Unset the old primary flag if there was one and it's different
        if (
          oldPrimaryId &&
          oldPrimaryId !== newPrimaryId &&
          state.entities[oldPrimaryId]
        ) {
          state.entities[oldPrimaryId]!.primary = false;
        }
        // Set the new primary flag
        state.entities[newPrimaryId]!.primary = true;
        // Update the primaryExamId state field
        state.primaryExamId = newPrimaryId;
      }
    },
  },
  // Listen for logout action from authSlice to clear exams data
  extraReducers: (builder) => {
    builder.addCase(logout, (state) => {
      examsAdapter.removeAll(state);
      state.primaryExamId = null;
      state.status = 'idle';
      state.error = null;
    });
  },
});

export const {
  examsLoading,
  examsReceived,
  examCreated,
  examUpdated,
  examDeleted,
  examsFailed,
  clearExams,
  setPrimaryExam,
} = examsSlice.actions;

// Export adapter selectors for easy data access
export const examsSelectors = {
  ...examsAdapter.getSelectors(
    (state: RootState) => state.exams || examsAdapter.getInitialState()
  ),
  selectExams: (state: RootState) =>
    examsAdapter
      .getSelectors()
      .selectAll(state.exams || examsAdapter.getInitialState()),
  selectPrimaryExam: (state: RootState) => {
    const examsState = state.exams || examsAdapter.getInitialState();
    const { primaryExamId, entities } = examsState;
    return primaryExamId ? entities[primaryExamId] : undefined;
  },
  selectPrimaryExamAccessLevel: createSelector(
    [
      (state: RootState) => {
        const examsState = state.exams || examsAdapter.getInitialState();
        const { primaryExamId, entities } = examsState;
        return primaryExamId ? entities[primaryExamId] : undefined;
      },
    ],
    (primaryExam): SubscriptionAccess => {
      return getExamAccessLevel(primaryExam);
    }
  ),
  selectStepExams: createSelector(
    [
      (state: RootState) =>
        examsAdapter
          .getSelectors()
          .selectAll(state.exams || examsAdapter.getInitialState()),
    ],
    (allExams) => {
      return allExams
        .filter(isStepExam)
        .sort((a, b) => a.name.localeCompare(b.name));
    }
  ),
  selectShelfExams: createSelector(
    [
      (state: RootState) =>
        examsAdapter
          .getSelectors()
          .selectAll(state.exams || examsAdapter.getInitialState()),
    ],
    (allExams) => {
      return allExams
        .filter(isShelfExam)
        .sort((a, b) => a.name.localeCompare(b.name));
    }
  ),
  selectSchoolExams: createSelector(
    [
      (state: RootState) =>
        examsAdapter
          .getSelectors()
          .selectAll(state.exams || examsAdapter.getInitialState()),
      (state: RootState) => state.school.school?.id,
    ],
    (allExams, currentSchoolId) => {
      return allExams
        .filter(
          (exam) =>
            isVerifiedSchoolExam(exam) && exam.school?.id === currentSchoolId
        )
        .sort((a, b) => {
          if (!a.examDate && !b.examDate) return 0;
          if (!a.examDate) return 1;
          if (!b.examDate) return -1;
          return (
            new Date(a.examDate).getTime() - new Date(b.examDate).getTime()
          );
        });
    }
  ),
  selectCustomExams: createSelector(
    [
      (state: RootState) =>
        examsAdapter
          .getSelectors()
          .selectAll(state.exams || examsAdapter.getInitialState()),
    ],
    (allExams) => {
      return allExams.filter(isCustomSchoolExam).sort((a, b) => {
        if (!a.examDate && !b.examDate) return 0;
        if (!a.examDate) return 1;
        if (!b.examDate) return -1;
        return new Date(a.examDate).getTime() - new Date(b.examDate).getTime();
      });
    }
  ),
  selectRCTExams: createSelector(
    [
      (state: RootState) =>
        examsAdapter
          .getSelectors()
          .selectAll(state.exams || examsAdapter.getInitialState()),
    ],
    (allExams) => {
      return allExams.filter(isRCTExam).sort((a, b) => {
        return a.name.localeCompare(b.name);
      });
    }
  ),
};
// Example usage: const allExams = useSelector(examsSelectors.selectAll);
// Example usage: const oneExam = useSelector(state => examsSelectors.selectById(state, examId));

export default examsSlice.reducer;
